import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import apiSet from 'api';
import axios from 'axios';
import { netLibConf } from 'consts/common';
import { ApiError, NotifyApiError } from 'errors/apiError';
import NotAuthorizedError from 'errors/notAuthorizedError';
import { EnelXGuiComponents } from 'gui';
import i18n from 'i18n';
import EnelXNet from 'net-com-lib';
import PropTypes from 'prop-types';
// import EnelXNet from 'remoteNet/EnelXNet'; // uncomment this for module federation
import { useTheme } from 'styled-components';
import { isArrayEmpty } from 'utils';
import * as notifyManager from 'utils/notifyManagement';
import { appName, defaultQueryProps } from 'utils/templating';

const { MigrationLoader } = EnelXGuiComponents;

const NetLib = EnelXNet(netLibConf);

export const Context = React.createContext({});

const refetchInterval = parseInt(
  process.env.TOKEN_REFRESH_MS_DELAY ?? '1200000',
  10
);

let interval: NodeJS.Timeout;

const showErrorPage = (err: unknown): void => {
  if (err instanceof NotAuthorizedError) throw err;
  throw new ApiError('Generic visibility error');
};

const ContextProvider = ({
  children,
}: {
  children: JSX.Element
}): React.ReactNode => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [api, setApi] = useState<any>(); // DA SISTEMARE
  const [ticketInfo, setTicketInfo] = useState({
    ecName: '',
    configName: '',
    podName: '',
    accountId: '',
    segment: ''
  });
  const { t } = useTranslation();
  const theme = useTheme();

  interface BaseInfo {
    accessToken: string
    uniqueId: string
  }

  const { data: baseInfo, refetch: refetchInfo } = useQuery(
    ['getTenantData@context'],
    async (): Promise<BaseInfo | undefined> => {
      try {
        const data = await NetLib.LoginUtils.getSessionDataFromLocalStorage();
        let toRet;
        if (data) {
          toRet = {
            accessToken: data.accessToken,
            uniqueId: data.uniqueid,
          };
        } else {
          showErrorPage(null);
          return undefined;
        }
        return toRet;
      } catch (err) {
        showErrorPage(err);
        return undefined;
      }
    },
    {
      ...defaultQueryProps,
    }
  );

  const { accessToken, uniqueId } = baseInfo ?? {};

  const apiWrap = async ({
    apiQueryCb,
    apiStringError,
    apiErrorCb,
    apiSuccessCb,
  }: {
    apiQueryCb: () => unknown
    apiStringError: string
    apiErrorCb: (err: unknown, errCb: () => void) => unknown
    apiSuccessCb: (result: unknown) => unknown
  }): Promise<null | unknown> => {
    try {
      const result = await apiQueryCb();
      if (apiSuccessCb) {
        apiSuccessCb(result);
      }
      return result;
    } catch (err) {
      let errorMessage = '';
      if (err instanceof NotifyApiError) {
        const { message } = err.exceptionCause;
        errorMessage =
          message || apiStringError || t('components.genericError');
      } else if (axios.isAxiosError(err)) {
        const { response } = err;
        const errorCode: string = response?.data?.errorCod ?? '';
        errorMessage =
          apiStringError ||
          response?.data?.status_code?.description ||
          response?.data?.description ||
          response?.data?.code ||
          (response?.data?.errorCode ? t(`errorCodes.${errorCode}`) : null) || // BE Ayesa with translation
          (Array.isArray(response?.data?.status_code?.errors) &&
            response?.data?.status_code?.errors?.join(', ')) ||
          t('components.genericError');
        if (err.response?.status === 403) {
          NetLib.LoginUtils.doLogout();
          NetLib.LoginUtils.doLogin(true, false, refetchInfo);
        }
      }
      if (apiErrorCb) {
        const errCb = (): void => {
          notifyManager.notifyError(errorMessage);
        };
        apiErrorCb(err, errCb);
      }
      notifyManager.notifyError(errorMessage);
      return err;
    }
  };

  const { data: csGrant } = useQuery(
    ['getCsGrant@energyCommunity', accessToken],
    async () => {
      try {
        const { data } = await NetLib.CsGrantApi.getCsGrantV2(
          accessToken,
          'REC_Tenant',
          {
            dashboard: 'ENERGY_COMMUNITY',
            overrideTenantId: true,
            isEC: true
          }
        );
        const isDataGrantEmpty = isArrayEmpty(data?.grant);
        const egIds = !isDataGrantEmpty ? data.grant.map((elem: { egId: string }) => elem.egId) : [];

        if (isDataGrantEmpty) {
          window.open(
            process.env.ENERGY_COMMUNITY_LINK,
            '_self',
          );
        }

        return {
          egIds,
          grants: data?.grant,
          userInfo: data?.userInfo,
        };
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 403) {
          NetLib.LoginUtils.doLogout();
          NetLib.LoginUtils.doLogin(true, false, refetchInfo);
        }
        showErrorPage(err);
        return undefined;
      }
    },
    {
      ...defaultQueryProps,
      enabled: !!accessToken,
    }
  );

  const { grants, egIds, userInfo } = csGrant ?? {};

  const { data: energyCommunities, } = useQuery(
    ['getEnergyCommunity@energyCommunity', baseInfo, egIds],
    async () => {
      try {
        const { data } = await NetLib.EnergyCommunitiesApi.getEnergyCommunities(
          accessToken,
          'ENERGY_COMMUNITY_IT',
          {
            egIds,
            pageNumber: 1,
            resultPerPage: 1000000,
            orderFields: 'ecId',
            direction: 'ASC',
            otm: true,
            dashboard: 'CER_DASHBOARD'
          }
        );

        if (!data?.energyCommunitiesOtm?.length) {
          throw new NotAuthorizedError('Not authorized', {
            reason: 'No energy community found for this user',
          });
        }
        return data ? data.energyCommunitiesOtm : undefined;
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 403) {
          NetLib.LoginUtils.doLogout();
          NetLib.LoginUtils.doLogin(true, false, refetchInfo);
        }
        showErrorPage(err);
        return undefined;
      }
    },
    {
      ...defaultQueryProps,
      enabled:
        !!accessToken &&
        !!egIds?.length &&
        !!api,
    }
  );

  const { data: roleBaseData } = useQuery(
    ['getRolebase@useRoleBase', uniqueId, accessToken, energyCommunities],
    async () => {
      const data = await api.rolebase.getRoleBase(accessToken);

      if (!data?.length || !data[0]?.rolebase?.routes?.length) {
        throw new NotAuthorizedError('Not authorized', {
          reason: 'No routes available',
        });
      }

      return data;
    },
    {
      ...defaultQueryProps,
      enabled: !!uniqueId && !!accessToken && !!api && !!energyCommunities,
    },
  );

  const condition =
    !!api &&
    !!accessToken &&
    !!uniqueId &&
    !!energyCommunities &&
    !!roleBaseData &&
    !!grants;

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  let value = {};
  if (condition) {
    value = {
      api,
      apiWrap,
      defaultQueryProps,
      notifyManager,
      accessToken,
      uniqueId,
      tenant: 'ENERGY_COMMUNITY_IT',
      energyCommunities,
      ticketInfo,
      setTicketInfo,
      grants,
      roleBaseData,
      user: {
        timeZone: 'Europe/Rome',
        isKiloBase: true,
        firstName: userInfo?.firstName,
        lastName: userInfo?.lastName,
      },
      localization: {
        countryFormatDate: 'DD-MM-YYYY',
        timeFormat: 'L HH:mm:ss',
        numbersFormat: 'it-IT',
        currency: 'EUR',
        countryID: i18n.language?.split('-')?.[0],
      },
    };
  }

  useEffect(() => {
    if (!NetLib.LoginUtils.alreadyLogged()) {
      NetLib.LoginUtils.doLogin(false, false, refetchInfo);
      return;
    }
    if (!accessToken || !uniqueId) return;

    const apiConfig = {
      netlib: NetLib,
      apiWrap,
      appName,
      notifyManager,
      uniqueId,
      accessToken,
      theme,
      tenant: 'ENERGY_COMMUNITY_IT',
      energyCommunities,
    };

    setApi(apiSet(apiConfig));
  }, [accessToken, uniqueId, csGrant, energyCommunities]);

  useEffect(() => {
    if (!api) return;
    if (interval) clearInterval(interval);
    interval = setInterval(() => api.auth.refreshToken, refetchInterval);
  }, [api]);

  return (
    <Context.Provider value={value}>
      {condition ? children : <MigrationLoader />}
    </Context.Provider>
  );
};

ContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ContextProvider;
